import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import styled from '@emotion/styled';

import { useI18n, useLogger } from '~Hooks';
import { Box } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';

import { MEMBERS_QUERY } from '~Components/LinkUser/api';
import {
  AllUsers,
  AllUsersVariables,
  AllUsers_allUsers,
} from '~Components/LinkUser/__queries__/AllUsers';
import { UserOrderByInput } from '../../../../__queries__/globalTypes';
import MemberRow from './MemberRow';

const MEMBERS_PER_PAGE = 50;

const Wrapper = styled(Box)`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 20px;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  mask-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 1) calc(100% - 20px),
    rgba(0, 0, 0, 0) 100%
  );
  -webkit-mask-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 1) calc(100% - 20px),
    rgba(0, 0, 0, 0) 100%
  );
`;

const LoadMoreSkeleton = styled.div`
  background-color: var(--colorBlueGray1100);
  border-radius: 8px;
  height: 48px;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 0.4;
    }
    50% {
      opacity: 0.1;
    }
  }
`;

interface Props {
  debouncedSearchValue: string;
  selectedMemberId: string | null;
  onSelectMember: (id: string) => void;
}

/** Handles fetching, pagination, and rendering of the members list */
const MembersList: FC<Props> = ({ debouncedSearchValue, selectedMemberId, onSelectMember }) => {
  const { i18n } = useI18n();
  const logger = useLogger('members-list');
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const [page, setPage] = useState(0);
  const [members, setMembers] = useState<AllUsers_allUsers[]>([]);
  // Track whether this is the very first load (no data ever fetched)
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Build search variables for the members query
  const buildSearchVariables = (pageOverride?: number): AllUsersVariables => ({
    filter: {
      deleted: false,
      ...(debouncedSearchValue && {
        OR: [{ name_contains: debouncedSearchValue }, { email_contains: debouncedSearchValue }],
      }),
    },
    skip: (pageOverride ?? page) * MEMBERS_PER_PAGE,
    first: MEMBERS_PER_PAGE,
    orderBy: UserOrderByInput.createdAt_DESC,
  });

  const [fetchMembers, membersQuery] = useLazyQuery<AllUsers, AllUsersVariables>(MEMBERS_QUERY, {
    fetchPolicy: 'no-cache',
  });

  // Reset and re-fetch when search changes
  useEffect(() => {
    logger.info('Search changed, resetting members list', { search: debouncedSearchValue });
    setMembers([]);
    setPage(0);
    fetchMembers({ variables: buildSearchVariables(0) });
  }, [debouncedSearchValue]);

  // Fetch more when page changes (not on initial load)
  useEffect(() => {
    if (page === 0 || membersQuery.loading) {
      return;
    }
    logger.info('Loading more members', { page });
    fetchMembers({ variables: buildSearchVariables() });
  }, [page]);

  // Append new results to the members list
  useEffect(() => {
    if (membersQuery.loading || !membersQuery.data) {
      return;
    }

    const newMembers = membersQuery.data.allUsers || [];
    setMembers((prev) => [...prev, ...newMembers]);
    setHasLoadedOnce(true);

    // Auto-select first member if none selected
    if (!selectedMemberId && newMembers.length > 0) {
      onSelectMember(newMembers[0].id);
    }
  }, [membersQuery.data]);

  // Initial load
  useEffect(() => {
    fetchMembers({ variables: buildSearchVariables(0) });
  }, []);

  // Determine if we can load more
  const showLoadMore =
    !membersQuery.loading && membersQuery.data
      ? !(members.length < MEMBERS_PER_PAGE * (page + 1))
      : false;

  const loadMore = useCallback(() => {
    setPage((curPage) => curPage + 1);
  }, []);

  // Intersection observer for infinite scroll
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && members.length > 24) {
        loadMore();
      }
    },
    [loadMore, members.length],
  );

  useEffect(() => {
    const option = {
      root: null,
      rootMargin: '0px',
      threshold: 1,
    };

    const observer = new IntersectionObserver(handleObserver, option);

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [handleObserver, showLoadMore]);

  // Show skeletons only when we have no rows to display yet (initial load or search reset)
  const showListLoading = membersQuery.loading && members.length === 0;

  // Show empty state only after a completed fetch returned nothing
  const showEmpty = !membersQuery.loading && hasLoadedOnce && members.length === 0;

  return (
    <Wrapper display="flex" flexDirection="column" gap="small">
      {showListLoading ? (
        <Box display="flex" flexDirection="column" gap="small">
          <LoadMoreSkeleton />
          <LoadMoreSkeleton />
          <LoadMoreSkeleton />
          <LoadMoreSkeleton />
          <LoadMoreSkeleton />
        </Box>
      ) : showEmpty ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          padding="xl"
          textAlign="center">
          <Heading level={3} color="var(--colorWhite)" bottom={8}>
            {i18n('CustomerForCheckout.NotFound')}
          </Heading>
          <Paragraph level={3} color="var(--colorBlueGray200)">
            {i18n('CustomerForCheckout.StartSearching')}
          </Paragraph>
        </Box>
      ) : (
        <>
          {members.map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              isSelected={selectedMemberId === member.id}
              onSelect={() => onSelectMember(member.id)}
            />
          ))}

          {/* Load more skeleton / infinite scroll trigger */}
          {(membersQuery.loading || showLoadMore) && (
            <Box display="flex" flexDirection="column" gap="small">
              <LoadMoreSkeleton />
              <LoadMoreSkeleton />
              <div ref={loadMoreRef}>
                <LoadMoreSkeleton />
              </div>
            </Box>
          )}
        </>
      )}
    </Wrapper>
  );
};

export default MembersList;
